import { GestureController, type GestureKind, type PointerPoint } from './gesture'

export interface InputHandlers {
  ping?(point: PointerPoint): void
  pick(point: PointerPoint, inspect?: boolean): string | null
  draggable(id: string): boolean
  hover(id: string | null, inspect?: boolean, point?: PointerPoint): void
  inspectMode(active: boolean): void
  click(id: string | null, point: PointerPoint, inspect: boolean): void
  drag(id: string, point: PointerPoint): void
  drop(id: string, point: PointerPoint): void
  orbit(dx: number, dy: number): void
  pan(dx: number, dy: number): void
  zoom(delta: number, point?: PointerPoint): void
  cancel(): void
  home(): void
  top(): void
  place?(shortcut: number): boolean
  gesture(kind: GestureKind): void
}

/** DOM input adapter. Camera gestures never become game actions. */
export class CanvasInput {
  private gesture = new GestureController()
  private pressedId: string | null = null
  private pointerId: number | null = null
  private allowClick = false
  private pingPress = false
  private inspectionPress = false
  private inspectionHeld = false
  private mode: 'orbit' | 'pan' = 'orbit'
  private spaceDown = false
  private touches = new Map<number, PointerPoint>()
  private multiTouch = false
  private lastPoint: PointerPoint | null = null
  private pointerInside = false
  private panKeys = new Set<string>()
  private keyFrame: number | null = null
  private keyTime = 0
  private listeners: (() => void)[] = []

  constructor(private canvas: HTMLCanvasElement, private handlers: InputHandlers) {
    this.listen('pointerdown', this.down)
    this.listen('pointermove', this.move)
    this.listen('pointerup', this.up)
    this.listen('pointercancel', this.cancelPointer)
    this.listen('lostpointercapture', this.cancelPointer)
    this.listen('pointerleave', () => {
      this.pointerInside = false
      handlers.inspectMode(false)
      handlers.hover(null, false)
    })
    this.listen('contextmenu', event => event.preventDefault())
    this.listen('wheel', event => {
      event.preventDefault()
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? canvas.getBoundingClientRect().height : 1
      const rect = canvas.getBoundingClientRect()
      handlers.zoom(event.deltaY * unit, { x: event.clientX - rect.left, y: event.clientY - rect.top })
    }, { passive: false })
    this.listen('keydown', this.key)
    this.listen('keyup', event => {
      if (event.code === 'Space' || event.key === ' ') this.spaceDown = false
      this.panKeys.delete(event.key.toLowerCase())
      if (!this.panKeys.size) this.stopKeyboard()
    })
    this.listen('blur', this.blur)
    window.addEventListener('blur', this.blur)
    this.listeners.push(() => window.removeEventListener('blur', this.blur))
    window.addEventListener('keydown', this.inspectKey)
    window.addEventListener('keyup', this.inspectKey)
    this.listeners.push(() => window.removeEventListener('keydown', this.inspectKey), () => window.removeEventListener('keyup', this.inspectKey))
  }

  private listen<K extends keyof HTMLElementEventMap>(name: K, fn: (event: HTMLElementEventMap[K]) => void, options?: AddEventListenerOptions): void {
    this.canvas.addEventListener(name, fn, options)
    this.listeners.push(() => this.canvas.removeEventListener(name, fn, options))
  }

  private point(event: PointerEvent): PointerPoint {
    const bounds = this.canvas.getBoundingClientRect()
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top }
  }

  private down = (event: PointerEvent): void => {
    if (event.button !== 0 && event.button !== 1 && event.button !== 2) return
    const point = this.point(event)
    this.lastPoint = point
    this.pointerInside = event.pointerType !== 'touch'
    if (event.pointerType === 'touch') {
      this.handlers.hover(null, false)
      this.touches.set(event.pointerId, point)
      if (this.touches.size > 1 || this.multiTouch) {
        // A second finger takes camera ownership and cancels a carried piece. It cannot drop it.
        this.multiTouch = true
        this.cancelSingle()
        event.preventDefault()
        this.canvas.setPointerCapture(event.pointerId)
        this.handlers.hover(null)
        this.handlers.gesture('pan')
        return
      }
    }
    const inspecting = event.altKey || event.metaKey
    this.setInspection(inspecting)
    const id = this.handlers.pick(point, inspecting)
    const forcedPan = event.button !== 0 || event.shiftKey || this.spaceDown
    const candidate = forcedPan || this.mode === 'pan' ? 'pan' : !inspecting && id && this.handlers.draggable(id) ? 'drag' : 'orbit'
    if (!this.gesture.begin(event.pointerId, point, candidate)) return
    event.preventDefault()
    this.canvas.focus({ preventScroll: true })
    this.pressedId = id
    this.pingPress = event.button === 0 && event.shiftKey && !inspecting && !this.spaceDown
    this.allowClick = !forcedPan
    this.inspectionPress = inspecting
    this.pointerId = event.pointerId
    this.canvas.setPointerCapture(event.pointerId)
  }

  private move = (event: PointerEvent): void => {
    const point = this.point(event)
    this.lastPoint = point
    this.pointerInside = event.pointerType !== 'touch'
    const inspecting = event.altKey || event.metaKey
    this.setInspection(inspecting)
    if (this.touches.has(event.pointerId)) {
      const before = this.touchPair()
      this.touches.set(event.pointerId, point)
      if (this.multiTouch) {
        const after = this.touchPair()
        if (before && after) {
          this.handlers.pan(after.x - before.x, after.y - before.y)
          if (before.distance > 4 && after.distance > 4) this.handlers.zoom(Math.log(before.distance / after.distance) * 1000, { x: after.x, y: after.y })
        }
        return
      }
    }
    if (this.pointerId === null) {
      if (event.pointerType !== 'touch') this.handlers.hover(this.handlers.pick(point, inspecting), inspecting, point)
      return
    }
    const movement = this.gesture.move(event.pointerId, point)
    if (!movement) return
    this.handlers.gesture(movement.kind)
    if (movement.kind === 'orbit') this.handlers.orbit(movement.dx, movement.dy)
    if (movement.kind === 'pan') this.handlers.pan(movement.dx, movement.dy)
    if (movement.kind === 'drag' && this.pressedId) this.handlers.drag(this.pressedId, point)
  }

  private up = (event: PointerEvent): void => {
    this.touches.delete(event.pointerId)
    if (this.multiTouch) {
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId)
      if (!this.touches.size) { this.multiTouch = false; this.handlers.gesture('idle') }
      return
    }
    const result = this.gesture.end(event.pointerId)
    if (!result) return
    const id = this.pressedId
    this.pointerId = null
    this.pressedId = null
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId)
    const point = this.point(event)
    const inspecting = event.altKey || event.metaKey
    if (result.kind === 'drag' && id) {
      if (inspecting) this.handlers.cancel()
      else this.handlers.drop(id, point)
    }
    else if (!result.moved && this.pingPress && event.button === 0) this.handlers.ping?.(point)
    else if (!result.moved && this.allowClick && event.button === 0) {
      // Releasing a reading modifier before the mouse must never convert that press into play.
      if (!this.inspectionPress || inspecting) this.handlers.click(id, point, inspecting)
      else this.handlers.inspectMode(false)
    }
    this.allowClick = false
    this.pingPress = false
    this.inspectionPress = false
    this.handlers.gesture('idle')
  }

  private touchPair(): { x: number; y: number; distance: number } | null {
    const [a, b] = [...this.touches.values()]
    return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, distance: Math.hypot(a.x - b.x, a.y - b.y) } : null
  }

  private cancelSingle(): void {
    if (this.pointerId === null) return
    const id = this.pointerId
    this.pointerId = null
    this.pressedId = null
    this.allowClick = false
    this.pingPress = false
    this.inspectionPress = false
    this.gesture.cancel()
    // Keep the first finger captured during a two-finger camera gesture.
    if (!this.multiTouch && this.canvas.hasPointerCapture(id)) this.canvas.releasePointerCapture(id)
    this.handlers.cancel()
    this.handlers.gesture('idle')
  }

  private cancel = (): void => {
    this.cancelSingle()
    const ids = [...this.touches.keys()]
    this.touches.clear()
    this.multiTouch = false
    for (const id of ids) if (this.canvas.hasPointerCapture(id)) this.canvas.releasePointerCapture(id)
    this.handlers.hover(null, false)
    this.handlers.gesture('idle')
  }

  private blur = (): void => { this.pointerInside = false; this.spaceDown = false; this.stopKeyboard(); this.setInspection(false); this.cancel() }
  private inspectKey = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null
    // A closed seat/option select can retain focus while the pointer is already back on the table.
    if (target?.isContentEditable || ['INPUT', 'TEXTAREA'].includes(target?.tagName ?? '')) {
      this.setInspection(false)
      return
    }
    const inspecting = event.altKey || event.metaKey
    if (!this.pointerInside || !this.lastPoint) { this.inspectionHeld = inspecting; return }
    this.setInspection(inspecting)
    this.refreshHover()
  }
  private cancelPointer = (event: PointerEvent): void => {
    if (event.pointerId === this.pointerId || this.touches.has(event.pointerId)) {
      this.pointerInside = false
      this.cancel()
    }
  }

  setMode(mode: 'orbit' | 'pan'): void { this.cancel(); this.mode = mode }
  reset(): void { this.cancel() }

  /** Re-pick only after the host has installed and drawn the latest safe projection. */
  refreshHover(): void {
    if (!this.pointerInside || !this.lastPoint || this.pointerId !== null || this.multiTouch) return
    this.handlers.inspectMode(this.inspectionHeld)
    this.handlers.hover(this.handlers.pick(this.lastPoint, this.inspectionHeld), this.inspectionHeld, this.lastPoint)
  }

  private setInspection(active: boolean): void {
    // Holding a reading modifier midway through a press or carry relinquishes action ownership.
    if (active && !this.inspectionPress && this.pointerId !== null) this.cancelSingle()
    this.inspectionHeld = active
    this.handlers.inspectMode(active)
  }

  private stopKeyboard(): void {
    this.panKeys.clear()
    if (this.keyFrame !== null) cancelAnimationFrame(this.keyFrame)
    this.keyFrame = null
    this.keyTime = 0
  }

  private panTick = (timestamp: number): void => {
    const seconds = this.keyTime ? Math.min(0.05, Math.max(0, (timestamp - this.keyTime) / 1000)) : 1 / 60
    this.keyTime = timestamp
    const dx = Number(this.panKeys.has('a')) - Number(this.panKeys.has('d'))
    const dy = Number(this.panKeys.has('w')) - Number(this.panKeys.has('s'))
    const distance = Math.hypot(dx, dy)
    if (distance) this.handlers.pan(dx / distance * seconds * 360, dy / distance * seconds * 360)
    this.keyFrame = requestAnimationFrame(this.panTick)
  }

  private key = (event: KeyboardEvent): void => {
    // This listener belongs to the focused canvas, never to document or an input field.
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (/^[1-9]$/.test(event.key) && this.handlers.place?.(Number(event.key))) { event.preventDefault(); return }
    if (event.code === 'Space' || event.key === ' ') { event.preventDefault(); this.spaceDown = true; return }
    const pans: Record<string, readonly [number, number]> = { a: [14, 0], d: [-14, 0], w: [0, 14], s: [0, -14] }
    const pan = pans[event.key.toLowerCase()]
    if (pan) {
      event.preventDefault()
      if (!this.panKeys.has(event.key.toLowerCase())) this.handlers.pan(...pan)
      this.panKeys.add(event.key.toLowerCase())
      if (this.keyFrame === null && typeof requestAnimationFrame !== 'undefined') this.keyFrame = requestAnimationFrame(this.panTick)
      return
    }
    const moves: Record<string, readonly [number, number]> = { ArrowLeft: [-30, 0], ArrowRight: [30, 0], ArrowUp: [0, -30], ArrowDown: [0, 30] }
    if (moves[event.key]) { event.preventDefault(); this.handlers.orbit(...moves[event.key]); return }
    if (event.key === '+' || event.key === '=') { event.preventDefault(); this.handlers.zoom(-140) }
    if (event.key === '-') { event.preventDefault(); this.handlers.zoom(140) }
    if (event.key.toLowerCase() === 't') { event.preventDefault(); this.handlers.top() }
    if (event.key === 'Home') { event.preventDefault(); this.handlers.home() }
    if (event.key === 'Escape') { this.cancel(); this.handlers.cancel() }
  }

  dispose(): void {
    this.blur()
    for (const remove of this.listeners) remove()
    this.listeners = []
  }
}
